import { MEDICATION_CODE_SYSTEMS } from '@liza/fhir-utils';
import type { ValueSet } from '@medplum/fhirtypes';

/**
 * RxNorm ingredient sets for the drug classes clinical protocols branch on ("is the patient on a
 * statin?", "on an agent that can cause hypoglycemia?"). Membership was taken from RxNav's RxClass
 * (`/rxclass/classMembers?relaSource=ATC`) for the ATC class named on each set, on 2026-09-05, and
 * is reproduced verbatim — including agents rarely or no longer prescribed — so a match never depends
 * on an editorial choice here. Matching is on the ingredient RxCUI; the protocol engine resolves a
 * `MedicationRequest`'s product code to its ingredients before comparing.
 */
interface RxNormClassSpec {
  name: string;
  title: string;
  atc: string;
  description: string;
  concepts: ReadonlyArray<{ code: string; display: string }>;
}

const buildValueSet = (spec: RxNormClassSpec): ValueSet => {
  const url = `https://liza.health/fhir/ValueSet/${spec.name}`;
  return {
    resourceType: 'ValueSet',
    name: spec.name,
    title: spec.title,
    url,
    identifier: [{ system: 'urn:ietf:rfc:3986', value: url }],
    version: '1',
    status: 'active',
    description: `${spec.description} RxNorm ingredients in ATC class ${spec.atc}, per RxNav RxClass.`,
    compose: {
      include: [{ system: MEDICATION_CODE_SYSTEMS.RXNORM, concept: spec.concepts.map((c) => ({ ...c })) }],
    },
  };
};

export const statinsValueSet = buildValueSet({
  name: 'rxnorm-statins',
  title: 'Statins (HMG-CoA reductase inhibitors)',
  atc: 'C10AA',
  description: 'Lipid-lowering statin ingredients.',
  concepts: [
    { code: '301542', display: 'rosuvastatin' },
    { code: '36567', display: 'simvastatin' },
    { code: '41127', display: 'fluvastatin' },
    { code: '42463', display: 'pravastatin' },
    { code: '596723', display: 'cerivastatin' },
    { code: '6472', display: 'lovastatin' },
    { code: '83367', display: 'atorvastatin' },
    { code: '861634', display: 'pitavastatin' },
  ],
});

export const insulinsValueSet = buildValueSet({
  name: 'rxnorm-insulins',
  title: 'Insulins and analogues',
  atc: 'A10A',
  description: 'Insulin ingredients, including fixed-dose combinations that contain an insulin.',
  concepts: [
    { code: '139825', display: 'insulin detemir' },
    { code: '1670007', display: 'insulin degludec' },
    { code: '1727493', display: 'insulin degludec / liraglutide' },
    { code: '1858994', display: 'insulin glargine / lixisenatide' },
    { code: '221109', display: 'insulin, regular, pork' },
    { code: '253182', display: 'insulin, regular, human' },
    { code: '2740394', display: 'insulin icodec' },
    { code: '274783', display: 'insulin glargine' },
    { code: '400008', display: 'insulin glulisine, human' },
    { code: '51428', display: 'insulin aspart, human' },
    { code: '86009', display: 'insulin lispro' },
  ],
});

export const sulfonylureasValueSet = buildValueSet({
  name: 'rxnorm-sulfonylureas',
  title: 'Sulfonylureas',
  atc: 'A10BB',
  description: 'Sulfonylurea ingredients — the non-insulin agents that can cause hypoglycemia.',
  concepts: [
    { code: '102846', display: 'glibornuride' },
    { code: '10633', display: 'tolazamide' },
    { code: '10635', display: 'tolbutamide' },
    { code: '173', display: 'acetohexamide' },
    { code: '2068', display: 'carbutamide' },
    { code: '2404', display: 'chlorpropamide' },
    { code: '25789', display: 'glimepiride' },
    { code: '25793', display: 'gliquidone' },
    { code: '4815', display: 'glyburide' },
    { code: '4816', display: 'gliclazide' },
    { code: '4821', display: 'glipizide' },
  ],
});

/** The RxCUIs in a drug-class ValueSet, for binding as a protocol-condition variable. */
export const valueSetRxcuis = (valueSet: ValueSet): string[] =>
  (valueSet.compose?.include ?? []).flatMap((inc) => (inc.concept ?? []).map((c) => c.code!));
